import { useEffect } from 'react'

/**
 * 自前実装 dropdown (= `MultiSelect` / `FilterMultiSelect`) 共通の dismiss 挙動。
 *
 * chakra v3 の `Menu` / `Combobox` namespace primitive は ecloud が react-19 版
 * chakra を re-export している都合で本 app (react-18) の JSX 型と噛み合わないため
 * 使えず、 開閉 state を自前管理する `Box` / `Flex` ベースの軽量 dropdown を各所で
 * 実装している。 その dropdown 群で共通の outside-click (document `mousedown` で
 * `rootRef` 外を検知) / Escape keydown による dismiss 挙動をこの hook に集約する。
 *
 * Escape の挙動: dropdown が open の時の Escape は「dropdown だけ閉じて親 modal は
 * 閉じない」 が正しい (= 親 Dialog / Popover に Escape が propagate すると 誤って
 * 親ごと閉じる UX 事故になる、 ワークフロー作成ダイアログの multi-select で観測
 * 済み)。 実装上のポイント: Zag JS (Chakra v3 の内部) の Dialog は
 * `trackEscapeKeydown` で **document の capture phase** に keydown listener を登録
 * する (`@zag-js/dismissable/dist/escape-keydown.js`)。 かつ Modal が先に open
 * するため Zag の listener が先に register される。 同じ document / capture 相の
 * listener は FIFO で発火するため、 dropdown 側を document capture で登録しても
 * Zag が先に走って Modal を閉じてしまう (試行 82bc956 / 522f17c8 で確認済み)。
 * そのため dropdown 側は **window の capture phase** で登録する。 event flow は
 * window capture → document capture → … と続くため、 window で catch すれば
 * Zag の document capture 前に stopPropagation でき、 Zag の handler は fire しない。
 *
 * @param rootRef dropdown のルート要素への ref。 外側クリック判定の基準にする
 * @param isOpen dropdown の開閉状態。 true の間のみ listener を登録する
 * @param onClose 外側クリック / Escape で呼ばれる close callback
 * @param menuRef body へ portal した menu 要素への ref。 portal した menu は
 *   `rootRef` の DOM subtree 外に出るため、 これを渡さないと menu 内の option
 *   クリックが「外側クリック」 と判定されて click 前に unmount される
 */
export const useDropdownDismiss = (
  rootRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
  menuRef?: React.RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    if (!isOpen) return
    const isInside = (target: Node): boolean =>
      (rootRef.current?.contains(target) ?? false) ||
      (menuRef?.current?.contains(target) ?? false)
    const handlePointerDown = (e: MouseEvent): void => {
      if (rootRef.current !== null && !isInside(e.target as Node)) {
        onClose()
      }
    }
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        // window capture 相で propagation を止めることで、 Zag Dialog の document
        // capture listener (`trackEscapeKeydown`) に Escape が到達しないように
        // する (= dropdown だけ閉じて親 modal は残す)。 window は document より
        // 外側なので capture 相では window の listener が先に fire する。
        e.stopPropagation()
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    // keydown は window の capture 相 (第 3 引数 true) で listen する。 document
    // capture (Zag Dialog が使う) より早く発火するため、 stopPropagation で Zag の
    // handler より前に event を止められる。
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [isOpen, rootRef, onClose, menuRef])
}
